/* eslint-disable react/prop-types */
import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import ReactApexCharts from "react-apexcharts";
import ApexCharts from "apexcharts";
import { seriesData } from "../constants/demoData";
import logo from "../assets/Pulsar_Logo_Color.svg";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";

const actions = [
  {
    label: "Blk",
    value: "Block",
  },
  {
    label: "Diff",
    value: "Difficulty",
  },
  {
    label: "Net",
    value: "Network",
  },
  {
    label: "Sup",
    value: "Supply",
  },
  {
    label: "Price",
    value: "Price",
  },
];

const LineChartPulsarStats = ({ stats }) => {
  const classes = useStyles()();
  const [activeTimeline, setActiveTimeline] = useState("Block");
  const [isChart, setIsChart] = useState(true);
  const [series, setSeries] = useState([...seriesData]);
  const [currentSeries, setCurrentSeries] = useState([{ data: [] }]);
  const [isStakeHistory, setIsStakeHistory] = useState(false);
  const stakeAmountMemo = useMemo(() => {
    if (stats?.data?.stake_pool?.total_staked_amount) {
      let parts = stats?.data?.stake_pool?.total_staked_amount.toFixed();
      parts = parts.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts;
    }
    return;
  }, [stats?.data?.stake_pool?.total_staked_amount]);

  // table data
  const columns = [
    { field: "date", headerName: "Date", minWidth: 200, flex: 1 },
    {
      field: isStakeHistory ? "stakes" : "rewards",
      headerName: isStakeHistory ? "Stakes" : "Rewards",
      minWidth: 150,
      flex: 1,
    },
  ];
  // stake rows

  const difficultyMemo = useMemo(() => {
    let difficultyArray = [{ data: [] }];
    if (stats?.length > 0) {
      const sortedData = [...stats]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .map((item) => [
          new Date(item.createdAt).getTime(),
          parseFloat(item.difficulty),
        ]);
      difficultyArray = [{ data: sortedData }];
    }
    return difficultyArray;
  }, [stats]);

  const blockMemo = useMemo(() => {
    let blockArray = [{ data: [] }];
    if (stats?.length > 0) {
      const sortedData = [...stats]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .map((item) => [
          new Date(item.createdAt).getTime(),
          parseInt(item.blockcount),
        ]);
      blockArray = [{ data: sortedData }];
    }
    return blockArray;
  }, [stats]);

  const priceMemo = useMemo(() => {
    let priceArray = [{ data: [] }];
    if (stats?.length > 0) {
      const sortedData = [...stats]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .map((item) => [
          new Date(item.createdAt).getTime(),
          parseFloat(item.lastPrice),
        ]);
      priceArray = [{ data: sortedData }];
    }
    return priceArray;
  }, [stats]);

  const networkMemo = useMemo(() => {
    let hashrateArray = [{ data: [] }];
    if (stats?.length > 0) {
      const sortedData = [...stats]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .map((item) => [
          new Date(item.createdAt).getTime(),
          parseFloat(item.hashrate),
        ]);
      hashrateArray = [{ data: sortedData }];
    }
    return hashrateArray;
  }, [stats]);

  const supplyMemo = useMemo(() => {
    let supplyArray = [{ data: [] }];
    if (stats?.length > 0) {
      const sortedData = [...stats]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .map((item) => [
          new Date(item.createdAt).getTime(),
          parseInt(item.supply),
        ]);
      supplyArray = [{ data: sortedData }];
    }
    return supplyArray;
  }, [stats]);

  // Dynamic chart options based on current series
  const options = useMemo(() => {
    const minTime =
      currentSeries[0]?.data?.[0]?.[0] || new Date("1 Nov 2022").getTime();
    const maxTime =
      currentSeries[0]?.data?.[currentSeries[0]?.data?.length - 1]?.[0] ||
      new Date().getTime();

    return {
      chart: {
        id: "explorer_chart",
        type: "area",
        height: 350,
        zoom: {
          autoScaleYaxis: true,
        },
      },
      colors: ["#0097ce"],
      annotations: {
        yaxis: [
          {
            y: 30,
            borderColor: "#999",
            label: {
              show: true,
              text: "Time",
              style: {
                color: "#fff",
                background: "#68beff",
              },
            },
          },
        ],
        xaxis: [
          {
            x: maxTime,
            borderColor: "#999",
            yAxisIndex: 0,
            label: {
              show: true,
              text: activeTimeline,
              style: {
                color: "#fff",
                background: "#0097ce",
              },
            },
          },
        ],
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
        style: "hollow",
      },
      xaxis: {
        type: "datetime",
        min: minTime,
        max: maxTime,
        tickAmount: 6,
        labels: {
          style: {
            colors: "#0097ce",
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#0097ce",
          },
        },
      },
      tooltip: {
        style: {
          color: "#000 !important",
        },
        x: {
          format: "dd MMM yyyy",
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100],
        },
      },
    };
  }, [currentSeries, activeTimeline]);

  const updateTimeline = useCallback(
    (type) => {
      setActiveTimeline(type);
      let seriesData;

      switch (type) {
        case "Difficulty":
          seriesData = difficultyMemo;
          break;
        case "Supply":
          seriesData = supplyMemo;
          break;
        case "Network":
          seriesData = networkMemo;
          break;
        case "Block":
          seriesData = blockMemo;
          break;
        case "Price":
          seriesData = priceMemo;
          break;
        default:
          seriesData = blockMemo;
          break;
      }

      if (seriesData[0]?.data?.length > 0) {
        // Update series immediately
        setCurrentSeries(seriesData);

        // Use setTimeout to ensure chart updates after series state is set
        setTimeout(() => {
          const minTime = seriesData[0]?.data?.[0]?.[0];
          const maxTime =
            seriesData[0]?.data?.[seriesData[0].data.length - 1]?.[0];

          if (minTime && maxTime) {
            // Force chart to update with proper zoom
            ApexCharts.exec("explorer_chart", "updateOptions", {
              xaxis: {
                type: "datetime",
                min: minTime,
                max: maxTime,
                tickAmount: 6,
              },
            });
          }
        }, 50);
      }
    },
    [difficultyMemo, supplyMemo, networkMemo, blockMemo, priceMemo],
  );

  // Set initial series when data loads
  useEffect(() => {
    if (blockMemo[0]?.data?.length > 0) {
      setCurrentSeries(blockMemo);
    }
  }, [blockMemo]);

  return (
    <div className={classes.chartWrap}>
      <Grid container direction={"row"} justifyContent="space-between">
        <div className="flex flex-wrap sm:flex-nowrap justify-between w-full mb-4">
          <h3 className="text-lg text-[#0097ce]">Explorer Statistics</h3>
          <div className={classes.timeToggler}>
            {actions.map((item) => (
              <Tooltip title={item.value} key={item.value}>
                <Button
                  sx={{
                    fontFamily: "AvenirNext-400 !important",
                    backgroundColor:
                      activeTimeline === item.value ? "#fff !important" : "",
                  }}
                  onClick={() => updateTimeline(item.value)}
                  type="button"
                >
                  {item.label}
                </Button>
              </Tooltip>
            ))}
          </div>
        </div>

        <ReactApexCharts
          className={classes.chart}
          options={options}
          series={currentSeries}
          type="area"
          height={440}
        />
      </Grid>
    </div>
  );
};

export default LineChartPulsarStats;

function useStyles() {
  return makeStyles((theme) => ({
    chartWrap: {
      flexGrow: 1,
      backgroundColor: theme.staking.chartWrap.background,
      width: "100%",
      minWidth: "25.875rem",
      padding: "12px !important",
      "&  *": {
        fontFamily: "AvenirNext-400 !important",
      },
      "& > div ": {
        minWidth: "100%",
        minHeight: "100%",
      },
    },
    subtitle: {
      maxHeight: "6.25rem",
      "@media screen and (max-width : 30.5625rem)": {
        padding: "0.5rem !important",
      },
      "& > div > img": {
        marginBottom: "-0.7rem",
        marginLeft: "-0.7rem",
      },
      "& > div > span": {
        color: theme.staking.p,
        fontFamily: "AvenirNext-400 !important",
        fontWeight: "bold",
      },
      "& > p ": {
        color: theme.staking.p,
        fontFamily: "AvenirNext-400 !important",
        fontWeight: "bolder",
        fontSize: "clamp(1.2rem, 4vw + 0.5rem, 1.3rem)",
      },
    },
    timeToggler: {
      display: "flex",
      flexFlow: "row nowrap",
      backgroundColor: theme.staking.timeToggler.background,
      alignItems: "center",
      maxHeight: "45px",
      padding: "0px 4px !important",
      borderRadius: "0.3125rem",
      "& > button": {
        color: `${theme.staking.p} !important`,
        padding: "8px 0px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        lineHeight: "1",
        cursor: "pointer",
        boxSizing: "border-box",
      },
      "@media screen and (max-width : 25rem)": {
        "& > button": {
          minWidth: "clamp(40px, 8vw + 1.5rem, 64px)",
        },
      },
      fontFamily: "AvenirNext-400 !important",
      "@media screen and (max-width : 30.5625rem)": {
        marginLeft: "0.5rem !important",
      },
    },
    chart: {
      width: "calc(100% + 1rem)",
      marginLeft: "-1rem",
      "& > .apexcharts-xaxis": {
        color: theme.palette.mode === "dark" ? "#fff" : "#000",
      },
    },
    statsToggle: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      maxHeight: "3.25rem",
      "& > button": {
        border: `1px solid ${theme.staking.p}`,
        color: theme.palette.mode === "dark" ? "" : theme.staking.p,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          scale: "1.05",
          transform: "scale(1.05)",
          transition: "all 0.2s ease-in-out",
        },
      },
    },
    table: {
      backgroundColor: theme.staking.card.background,
      minHeight: "27.0625rem",
      width: "100%",
      marginBottom: "1.5rem",
    },
  }));
}
